import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutGrid,
    CheckSquare,
    BookOpen,
    Bell,
    Shield,
    BarChart3,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

const activityItems = [
    { icon: LayoutGrid, label: 'Overview', to: '/admin/activity-centre' },
    { icon: BookOpen, label: 'Courses', to: '/admin/activity-centre/courses' },
    { icon: CheckSquare, label: 'Tasks', to: '/admin/activity-centre/tasks' },
    { icon: Bell, label: 'Notices', to: '/admin/activity-centre/notices' },
    { icon: BarChart3, label: 'Surveys', to: '/admin/activity-centre/surveys' },
    { icon: Shield, label: 'Compliance', to: '/admin/activity-centre/compliance' },
];

const ActivityCentreSidebar = ({ sidebarCollapsed }) => {
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Only show when on an activity-centre route
    const isVisible = location.pathname.startsWith('/admin/activity-centre');

    if (!isVisible) return null;

    const mainSidebarWidth = sidebarCollapsed ? 80 : 256;

    return (
        <>
            {/* Secondary sidebar panel */}
            <div
                className="activity-centre-sidebar"
                style={{
                    position: 'fixed',
                    left: `${mainSidebarWidth}px`,
                    bottom: '24px',
                    width: isCollapsed ? '0px' : '200px',
                    height: 'auto',
                    maxHeight: '55vh',
                    backgroundColor: 'var(--card)',
                    borderRadius: '0 12px 12px 0',
                    boxShadow: isCollapsed
                        ? 'none'
                        : '4px 0 24px rgba(0, 0, 0, 0.06), 2px 0 8px rgba(0, 0, 0, 0.03)',
                    zIndex: 45,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRight: isCollapsed ? 'none' : '1px solid var(--border)',
                    borderTop: isCollapsed ? 'none' : '1px solid var(--border)',
                    borderBottom: isCollapsed ? 'none' : '1px solid var(--border)',
                    overflow: 'hidden',
                    pointerEvents: isCollapsed ? 'none' : 'auto',
                }}
            >
                {/* Navigation Items */}
                <nav
                    style={{
                        padding: '12px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        opacity: isCollapsed ? 0 : 1,
                        transition: 'opacity 0.2s ease',
                        minWidth: '184px',
                    }}
                >
                    {activityItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === '/admin/activity-centre'}
                            style={({ isActive }) => ({
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontSize: '14px',
                                fontWeight: isActive ? 600 : 400,
                                color: isActive ? 'var(--accent)' : 'var(--foreground)',
                                backgroundColor: isActive ? 'var(--secondary)' : 'transparent',
                                borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                                transition: 'all 0.15s ease',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            })}
                            onMouseEnter={(e) => {
                                const borderColor = e.currentTarget.style.borderLeftColor;
                                if (borderColor !== 'var(--accent)' && borderColor !== '#e86f4a' && borderColor !== 'rgb(232, 111, 74)') {
                                    e.currentTarget.style.backgroundColor = 'var(--secondary)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                const borderColor = e.currentTarget.style.borderLeftColor;
                                if (borderColor !== 'var(--accent)' && borderColor !== '#e86f4a' && borderColor !== 'rgb(232, 111, 74)') {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                }
                            }}
                        >
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Collapse button — outside the overflow area */}
                {!isCollapsed && (
                    <button
                        onClick={() => setIsCollapsed(true)}
                        style={{
                            position: 'absolute',
                            right: '8px',
                            bottom: '8px',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--card)',
                            border: '1px solid var(--border)',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 50,
                            transition: 'all 0.2s ease',
                            padding: 0,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--secondary)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.12)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--card)';
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
                        }}
                        title="Collapse sidebar"
                    >
                        <ChevronLeft size={14} color="var(--muted-foreground)" />
                    </button>
                )}
            </div>

            {/* Expand button — shown on primary sidebar edge when collapsed */}
            {isCollapsed && (
                <button
                    onClick={() => setIsCollapsed(false)}
                    style={{
                        position: 'fixed',
                        left: `${mainSidebarWidth - 1}px`,
                        bottom: '80px',
                        width: '28px',
                        height: '28px',
                        borderRadius: '0 50% 50% 0',
                        backgroundColor: 'var(--card)',
                        borderTop: '1px solid var(--border)',
                        borderRight: '1px solid var(--border)',
                        borderBottom: '1px solid var(--border)',
                        borderLeft: 'none',
                        boxShadow: '2px 2px 8px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 50,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        padding: 0,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--secondary)';
                        e.currentTarget.style.boxShadow = '2px 2px 12px rgba(232,111,74,0.2)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--card)';
                        e.currentTarget.style.boxShadow = '2px 2px 8px rgba(0,0,0,0.1)';
                    }}
                    title="Expand Activity Centre sidebar"
                >
                    <ChevronRight size={14} color="var(--accent)" />
                </button>
            )}
        </>
    );
};

export default ActivityCentreSidebar;
